import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, refreshLogDerivedData } from "@/lib/queryClient";
import type { ChatMessage } from "@shared/schema";
import { Send, Mic, MicOff, AlertCircle, RotateCcw, ImagePlus, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatTypographicQuotes } from "@/lib/typographic-quotes";

const ROTATING_PLACEHOLDERS = [
  "What's on your mind...",
  "How did today go...",
  "Anything you want to look at...",
  "What are you noticing...",
  "What's been sitting with you...",
  "How are you, really...",
];

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

function getPlaceholder(): string {
  const today = new Date();
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000
  );
  return ROTATING_PLACEHOLDERS[dayOfYear % ROTATING_PLACEHOLDERS.length];
}

// ── Particle canvas ──────────────────────────────────────────────────────────
// Same geometry and colour ramp as Home's ParticleField, at 40 % opacity —
// atmosphere behind the conversation, not a data visualisation.

function ChatParticleField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const FILL = 0.72;
  const COUNT = 8000;
  const OPACITY_SCALE = 0.4;

  const points = useMemo(() => {
    const result: Array<{ ph: number; lat: number; f: number; j: number; order: number; band: number }> = [];
    let seed = 3313;
    const random = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
    for (let index = 0; index < COUNT; index++) {
      const theta = Math.acos(1 - 2 * ((index + 0.5) / COUNT));
      const phase = Math.PI * (1 + Math.sqrt(5)) * index;
      const latitude = Math.PI / 2 - theta;
      const longitude = phase % (Math.PI * 2);
      const field = 0.55 + 0.45 * (
        Math.sin(longitude * 3.1 + latitude * 2.2) * 0.5
        + Math.cos(longitude * 5.7 - latitude * 1.4) * 0.3
        + Math.sin(longitude * 9.3 + latitude * 4.9) * 0.2
      );
      result.push({ ph: phase, lat: latitude, f: field, j: random(), order: random(), band: Math.floor(((latitude + Math.PI / 2) / Math.PI) * 7) });
    }
    return result;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let raf = 0;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      const r = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * r;
      canvas.height = window.innerHeight * r;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };

    const draw = (now: number) => {
      const r = Math.min(window.devicePixelRatio || 1, 2);
      const W = window.innerWidth, H = window.innerHeight;
      ctx.setTransform(r, 0, 0, r, 0, 0);
      ctx.clearRect(0, 0, W, H);

      const cx = W / 2, cy = H * 0.34, scale = Math.min(W, H) * 0.44;
      const yaw = reduced ? 0 : now * 0.00006, pitch = 0.32;
      const project = (x: number, y: number, z: number) => {
        const rx = x * Math.cos(yaw) - z * Math.sin(yaw);
        let rz = x * Math.sin(yaw) + z * Math.cos(yaw);
        const ry = y * Math.cos(pitch) - rz * Math.sin(pitch);
        rz = y * Math.sin(pitch) + rz * Math.cos(pitch);
        const p = 1 / (2.1 - rz * 0.5);
        return [cx + rx * scale * p * 1.7, cy + ry * scale * p * 1.7, rz, p] as const;
      };

      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = `rgba(223,232,238,${0.10 * OPACITY_SCALE})`;
      ctx.lineWidth = 0.6;
      for (const lat of [-60, -30, 0, 30, 60]) {
        ctx.beginPath();
        let drawing = false;
        for (let lon = 0; lon <= 360; lon += 6) {
          const la = lat * Math.PI / 180, lo = lon * Math.PI / 180;
          const pr = project(Math.cos(la) * Math.cos(lo), Math.sin(la), Math.cos(la) * Math.sin(lo));
          if (pr[2] < -0.1) { drawing = false; continue; }
          if (!drawing) ctx.moveTo(pr[0], pr[1]); else ctx.lineTo(pr[0], pr[1]);
          drawing = true;
        }
        ctx.stroke();
      }

      ctx.globalCompositeOperation = "lighter";
      for (const pt of points) {
        const bandLat = (pt.band / 6) * Math.PI - Math.PI / 2;
        const lat = pt.lat + (bandLat - pt.lat) * 0.7;
        const theta = Math.PI / 2 - lat;
        const radius = 1 + (pt.f - 0.5) * 0.62;
        const pr = project(Math.sin(theta) * Math.cos(pt.ph) * radius, Math.cos(theta) * radius, Math.sin(theta) * Math.sin(pt.ph) * radius);
        if (pr[2] < -0.16) continue;

        const depth = (pr[2] + 1) / 2;
        const rim = Math.pow(1 - Math.abs(pr[2]), 2.6);
        const filled = pt.order < Math.min(0.95, FILL);
        const cool = [110, 200, 235], warm = [255, 150, 90], bone = [228, 236, 242];
        const endpoint = pt.f < 0.5 ? cool : warm;
        const blend = Math.abs(pt.f - 0.5) * 2;
        const color = [0, 1, 2].map(i => endpoint[i] + (bone[i] - endpoint[i]) * (1 - blend));
        const gray = (color[0] + color[1] + color[2]) / 3;
        const final = color.map(c => gray + (c - gray) * 1.0);
        let opacity = ((0.20 + pt.j * 0.42) * (0.26 + depth * 0.82) + rim * 0.26) * OPACITY_SCALE;
        if (!filled) opacity *= 0.16;
        if (opacity < 0.005) continue;

        ctx.fillStyle = `rgba(${filled ? final[0] : 150},${filled ? final[1] : 165},${filled ? final[2] : 180},${opacity})`;
        const sz = Math.max(0.6, (0.7 + pt.j * 0.9) * pr[3] * 1.35);
        ctx.fillRect(pr[0], pr[1], sz, sz);
      }

      ctx.globalCompositeOperation = "source-over";
      if (!reduced) raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    draw(performance.now());
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [points]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}
      aria-hidden="true"
      data-testid="chat-particle-canvas"
    />
  );
}

// ── Time separator helpers ────────────────────────────────────────────────────

function formatSeparatorTime(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (msgDay === today) return time;
  if (msgDay === yesterday) return `Yesterday · ${time}`;
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${time}`;
}

type MessageItem =
  | { kind: "separator"; label: string; key: string }
  | { kind: "message"; msg: ChatMessage; isNewGroup: boolean; idx: number };

function buildMessageItems(messages: ChatMessage[]): MessageItem[] {
  const items: MessageItem[] = [];
  let lastTime: number | null = null;
  let lastRole: string | null = null;
  let idx = 0;
  for (const msg of messages) {
    const t = new Date(msg.createdAt).getTime();
    if (lastTime !== null && t - lastTime > 30 * 60 * 1000) {
      items.push({ kind: "separator", label: formatSeparatorTime(new Date(t)), key: `sep-${t}` });
      lastRole = null;
    }
    items.push({ kind: "message", msg, isNewGroup: msg.role !== lastRole, idx });
    lastTime = t;
    lastRole = msg.role;
    idx++;
  }
  return items;
}

// ── Chat page ─────────────────────────────────────────────────────────────────

export default function Chat() {
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [greetingRequested, setGreetingRequested] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<string[]>([]);
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const recognitionRef = useRef<any>(null);
  const baseTextRef = useRef("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const MAX_IMAGES = 4;
  const MAX_DIMENSION = 1600;
  const JPEG_QUALITY = 0.8;
  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  const compressImage = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas not supported")); return; }
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        URL.revokeObjectURL(objUrl);
        resolve(dataUrl);
      };
      img.onerror = () => { URL.revokeObjectURL(objUrl); reject(new Error("Failed to load image")); };
      const objUrl = URL.createObjectURL(file);
      img.src = objUrl;
    });
  }, []);

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const toProcess = files.slice(0, MAX_IMAGES - pendingImages.length);
    for (const file of toProcess) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > MAX_FILE_SIZE) {
        toast({ title: "Image too large", description: "Max 20 MB per image.", variant: "destructive" });
        continue;
      }
      try {
        const compressed = await compressImage(file);
        setPendingImages(prev => prev.length >= MAX_IMAGES ? prev : [...prev, compressed]);
      } catch {
        toast({ title: "Couldn't process image", description: "Try a different image.", variant: "destructive" });
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [pendingImages.length, toast, compressImage]);

  const removePendingImage = useCallback((index: number) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const hasSpeechSupport = !!getSpeechRecognition();

  useEffect(() => {
    return () => { if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; } };
  }, []);

  const toggleListening = useCallback(() => {
    const SR = getSpeechRecognition();
    if (!SR) return;
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop(); recognitionRef.current = null; setIsListening(false); return;
    }
    const recognition = new SR();
    recognition.continuous = true; recognition.interimResults = true; recognition.lang = "en-US";
    baseTextRef.current = input;
    recognition.onresult = (event: any) => {
      let finalParts = "", interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalParts += t + " "; else interim = t;
      }
      const base = baseTextRef.current;
      const spacer = base && !base.endsWith(" ") ? " " : "";
      setInput((base + spacer + finalParts + interim).replace(/\s+/g, " ").trim());
      if (finalParts) baseTextRef.current = (base + spacer + finalParts).replace(/\s+/g, " ").trim();
    };
    recognition.onerror = (event: any) => {
      setIsListening(false); recognitionRef.current = null;
      const err = event?.error || "unknown";
      if (err === "not-allowed") toast({ title: "Microphone access denied", description: "Allow microphone access in browser settings.", variant: "destructive" });
      else if (err === "network") toast({ title: "Voice input unavailable", description: "Network connection required.", variant: "destructive" });
      else if (err !== "no-speech" && err !== "aborted") toast({ title: "Voice input failed", description: "Something went wrong. Try again.", variant: "destructive" });
    };
    recognition.onend = () => { setIsListening(false); recognitionRef.current = null; inputRef.current?.focus(); };
    recognitionRef.current = recognition;
    try { recognition.start(); setIsListening(true); }
    catch { setIsListening(false); recognitionRef.current = null; toast({ title: "Voice input not available", description: "Your browser may not support voice input.", variant: "destructive" }); }
  }, [isListening, input]);

  const { data: messages = [], isLoading } = useQuery<ChatMessage[]>({ queryKey: ["/api/chat"] });

  const greetingMutation = useMutation({
    mutationFn: async () => {
      const now = new Date();
      const res = await apiRequest("POST", "/api/chat/greeting", {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        localDateTime: `${now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at ${now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`,
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.needsGreeting && data.greeting) queryClient.invalidateQueries({ queryKey: ["/api/chat"] });
    },
  });

  useEffect(() => {
    if (!isLoading && !greetingRequested) { setGreetingRequested(true); greetingMutation.mutate(); }
  }, [isLoading]);

  const sendMutation = useMutation({
    mutationFn: async (payload: { message: string; images?: string[]; timezone?: string; localDateTime?: string }) => {
      const res = await apiRequest("POST", "/api/chat", payload);
      return res.json() as Promise<ChatMessage>;
    },
    onMutate: () => setIsTyping(true),
    onSuccess: (assistantMsg) => {
      queryClient.setQueryData<ChatMessage[]>(["/api/chat"], (old = []) => (
        old.some(message => message.id === assistantMsg.id)
          ? old
          : [...old, assistantMsg]
      ));
      queryClient.invalidateQueries({ queryKey: ["/api/chat"] });
      refreshLogDerivedData();
      setIsTyping(false); setFailedMessage(null); setFailedImages([]);
    },
    onError: (error: Error) => {
      setIsTyping(false);
      if (error.message?.includes("401")) {
        queryClient.setQueryData<ChatMessage[]>(["/api/chat"], (old = []) => old.filter(m => (m as any).id !== -1));
        toast({ title: "Session expired", description: "Please log in again to continue.", variant: "destructive" });
        setTimeout(() => { window.location.href = "/auth"; }, 1500);
      } else if (error.message?.includes("429")) {
        let limitMsg = "You've reached today's limit. Come back tomorrow.";
        try { const p = JSON.parse(error.message.substring(error.message.indexOf("{"))); if (p.error) limitMsg = p.error; } catch {}
        queryClient.setQueryData<ChatMessage[]>(["/api/chat"], (old = []) => [...old.filter(m => (m as any).id !== -1), { id: -2, userId: "", role: "assistant", content: limitMsg, images: [], createdAt: new Date() } as any]);
      } else {
        queryClient.setQueryData<ChatMessage[]>(["/api/chat"], (old = []) => old.filter(m => (m as any).id !== -1));
        setFailedMessage(failedMessage || "");
        toast({ title: "Message not sent", description: "Something went wrong. Try again.", variant: "destructive" });
      }
    },
  });

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
    const t = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(t);
  }, [messages, isTyping, scrollToBottom]);

  const handleSend = (retryMsg?: string, retryImages?: string[]) => {
    const msg = retryMsg || input.trim();
    const imgs = retryImages || pendingImages;
    if ((!msg && !imgs.length) || sendMutation.isPending) return;
    if (isListening && recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; setIsListening(false); }
    if (!retryMsg) setInput("");
    if (!retryImages) setPendingImages([]);
    setFailedMessage(msg); setFailedImages(imgs);
    queryClient.setQueryData<ChatMessage[]>(["/api/chat"], (old = []) => [...old, { id: -1, userId: "", role: "user", content: msg || "(shared an image)", images: imgs, createdAt: new Date() } as any]);
    const now = new Date();
    sendMutation.mutate({
      message: msg,
      images: imgs.filter(i => i.startsWith("data:image/")).length > 0 ? imgs.filter(i => i.startsWith("data:image/")) : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      localDateTime: `${now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at ${now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`,
    });
    if (inputRef.current) inputRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isWaitingForGreeting = greetingMutation.isPending && messages.length === 0;
  const hasMessages = messages.length > 0;
  const placeholder = getPlaceholder();
  const messageItems = buildMessageItems(messages);
  const canSend = (input.trim() || pendingImages.length > 0) && !sendMutation.isPending;

  return (
    <div
      className="chat-root"
      data-testid="chat-page"
    >
      {/* Particle atmosphere */}
      <ChatParticleField />

      {/* Bottom scrim — same height as Home's, matches specified gradient */}
      <div className="chat-scrim" aria-hidden="true" />

      {/* Page shell: scrollable messages + sticky input */}
      <div className="chat-shell">

        {/* ── Messages ─────────────────────────────────────────────────── */}
        <div
          ref={scrollContainerRef}
          className="chat-scroll"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="chat-inner">

            {/* Loading */}
            {(isLoading || isWaitingForGreeting) && (
              <div className="chat-state-center" data-testid="chat-loading">
                <div className="chat-breath-line" />
                <span className="chat-state-label">
                  {isWaitingForGreeting ? "Fractal is thinking…" : "Loading…"}
                </span>
              </div>
            )}

            {/* Empty */}
            {!isLoading && !isWaitingForGreeting && !hasMessages && (
              <div className="chat-state-center chat-msg-entry" data-testid="chat-empty-state">
                <div className="chat-breath-dot" />
                <p className="chat-empty-headline">I'm here.</p>
                <p className="chat-empty-sub">whenever you're ready</p>
              </div>
            )}

            {/* Message list */}
            {messageItems.map((item) => {
              if (item.kind === "separator") {
                return (
                  <div key={item.key} className="chat-separator">
                    <span>{item.label}</span>
                  </div>
                );
              }

              const { msg, isNewGroup, idx } = item;
              const animStyle = { animationDelay: `${Math.min(idx * 0.025, 0.25)}s` };
              const groupClass = isNewGroup ? "chat-group-start" : "chat-group-cont";

              if (msg.role === "user") {
                return (
                  <div
                    key={msg.id === -1 ? `optimistic-${idx}` : msg.id}
                    className={`chat-row chat-row-user ${groupClass} chat-msg-entry`}
                    style={animStyle}
                    data-testid={`chat-message-user-${msg.id === -1 ? "pending" : msg.id}`}
                  >
                    <div className="chat-bubble-user">
                      {/* Images */}
                      {(msg as any).images?.length > 0 && (
                        <div className={`chat-img-grid ${msg.content ? "mb-2" : ""}`}>
                          {((msg as any).images as string[]).map((img, k) => (
                            <img
                              key={k}
                              src={img}
                              alt="Shared image"
                              className="chat-img-thumb"
                              onClick={() => window.open(img, "_blank")}
                              data-testid={`img-chat-${msg.id}-${k}`}
                            />
                          ))}
                        </div>
                      )}
                      {msg.content && (
                        <p
                          className="chat-text-user"
                          data-testid={`text-chat-message-${msg.id === -1 ? "pending" : msg.id}`}
                        >
                          {msg.content}
                        </p>
                      )}
                    </div>
                  </div>
                );
              }

              // AI assistant message
              return (
                <div
                  key={msg.id === -1 ? `optimistic-${idx}` : msg.id}
                  className={`chat-row chat-row-ai ${groupClass} chat-msg-entry`}
                  style={animStyle}
                  data-testid={`chat-message-assistant-${msg.id === -1 ? "pending" : msg.id}`}
                >
                  <div className="chat-bubble-ai">
                    {isNewGroup && (
                      <div className="chat-ai-label" data-testid="text-mirror-label">Fractal</div>
                    )}
                    {msg.content && (
                      <p
                        className="chat-text-ai"
                        data-testid={`text-chat-message-${msg.id === -1 ? "pending" : msg.id}`}
                      >
                        {formatTypographicQuotes(msg.content)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {isTyping && (
              <div className="chat-row chat-row-ai chat-group-start chat-msg-entry" data-testid="chat-typing-indicator">
                <div className="chat-bubble-ai">
                  <div className="chat-ai-label">Fractal</div>
                  <div className="typing-dots">
                    <span className="typing-dot" style={{ animationDelay: "0s" }} />
                    <span className="typing-dot" style={{ animationDelay: "0.16s" }} />
                    <span className="typing-dot" style={{ animationDelay: "0.32s" }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} style={{ height: "1px" }} />
          </div>
        </div>

        {/* ── Input bar ─────────────────────────────────────────────────── */}
        <div className="chat-input-bar">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleImageSelect}
            data-testid="input-image-file"
          />

          {/* Pending image previews */}
          {pendingImages.length > 0 && (
            <div className="chat-pending-images">
              {pendingImages.map((img, i) => (
                <div key={i} className="chat-pending-thumb-wrap">
                  <img src={img} alt={`Pending ${i + 1}`} className="chat-pending-thumb" data-testid={`img-pending-${i}`} />
                  <button onClick={() => removePendingImage(i)} className="chat-pending-remove touch-target" data-testid={`button-remove-image-${i}`}>
                    <span className="chat-pending-remove-inner"><X size={10} /></span>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="chat-input-row">
            {/* Accessory buttons */}
            <div className="chat-accessories">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={sendMutation.isPending || pendingImages.length >= MAX_IMAGES}
                data-testid="button-add-image"
                className="chat-accessory-btn touch-target"
                style={pendingImages.length > 0 ? { borderColor: "var(--accent-border-strong)", color: "var(--accent-color)", background: "var(--accent-bg-strong)" } : {}}
              >
                <ImagePlus size={15} />
              </button>
              {hasSpeechSupport && (
                <button
                  onClick={toggleListening}
                  disabled={sendMutation.isPending}
                  data-testid="button-voice-input"
                  className="chat-accessory-btn touch-target"
                  style={isListening ? { borderColor: "var(--accent-border-strong)", color: "var(--accent-color)", background: "var(--accent-bg-strong)", animation: "gentle-pulse 1.4s ease-in-out infinite" } : {}}
                >
                  {isListening ? <MicOff size={15} /> : <Mic size={15} />}
                </button>
              )}
            </div>

            {/* Text input */}
            <div
              className="chat-textarea-wrap"
              style={isListening ? { borderColor: "var(--accent-border)", background: "var(--accent-bg)" } : {}}
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sendMutation.isPending}
                placeholder={isListening ? "Listening..." : placeholder}
                rows={1}
                data-testid="input-chat-message"
                className="chat-textarea"
                onInput={(e) => {
                  const t = e.target as HTMLTextAreaElement;
                  t.style.height = "0";
                  t.style.height = Math.min(t.scrollHeight, 100) + "px";
                }}
                onFocus={() => {
                  setTimeout(() => {
                    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                  }, 300);
                }}
              />
            </div>

            {/* Send */}
            <button
              onClick={() => handleSend()}
              disabled={!canSend}
              data-testid="button-send-chat"
              className="chat-send-btn touch-target"
              style={canSend ? { borderColor: "var(--accent-border-strong)", background: "var(--accent-bg-strong)" } : {}}
            >
              <Send size={15} style={{ color: "var(--accent-color)" }} />
            </button>
          </div>
        </div>
      </div>

      <style>{chatStyles}</style>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const chatStyles = `
  /* Root — Chat is always on #04060A, so pin accent tokens to dark-mode
     values regardless of the system/app theme class on <html>. */
  .chat-root {
    position: relative;
    height: 100%;
    flex: 1 1 0;
    overflow: hidden;
    background: #04060A;
    /* Use the shared on-dark sage token even when the app theme is light. */
    --accent-color: var(--accent-on-dark);
    --accent-color-soft: color-mix(in srgb, var(--accent-color) 70%, transparent);
    --accent-bg: color-mix(in srgb, var(--accent-color) 6%, transparent);
    --accent-bg-strong: color-mix(in srgb, var(--accent-color) 11%, transparent);
    --accent-border: color-mix(in srgb, var(--accent-color) 15%, transparent);
    --accent-border-strong: color-mix(in srgb, var(--accent-color) 26%, transparent);
    --accent-text-soft: color-mix(in srgb, var(--accent-color) 78%, transparent);
    --border-hairline: rgba(223,232,238,0.09);
    --border-light: rgba(223,232,238,0.12);
    --fg-raw: #DFE8EE;
    --text-primary: #F1F4F6;
    --text-secondary: rgba(223,232,238,0.75);
    --text-muted: rgba(223,232,238,0.45);
  }

  /* Bottom scrim — identical spec to Home's but with Chat's bg colour */
  .chat-scrim {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    height: 58%;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(
      to top,
      #04060A 6%,
      rgba(4,6,10,.88) 32%,
      rgba(4,6,10,.45) 64%,
      transparent
    );
  }

  /* Shell: fills root, stacks over particle + scrim */
  .chat-shell {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    flex-direction: column;
  }

  /* Scroll container */
  .chat-scroll {
    flex: 1 1 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .chat-inner {
    max-width: 42rem;
    margin: 0 auto;
    padding: 2.5rem 1.25rem 1.5rem;
  }
  @media (min-width: 640px) {
    .chat-inner { padding: 3rem 1.5rem 2rem; }
  }

  /* ── Message rows ── */
  .chat-row {
    display: flex;
  }
  .chat-row-user { justify-content: flex-end; }
  .chat-row-ai   { justify-content: flex-start; }

  /* Spacing: tight when consecutive, generous on speaker change */
  .chat-row.chat-group-start { margin-top: 1.25rem; }
  .chat-row.chat-group-cont  { margin-top: 0.3rem; }
  .chat-row:first-of-type   { margin-top: 0; }

  /* ── User bubble ── */
  .chat-bubble-user {
    max-width: 80%;
    padding: 10px 14px;
    border-radius: 14px 14px 3px 14px;
    background: var(--accent-bg-strong);
    border: 1px solid var(--accent-border);
  }
  .chat-text-user {
    margin: 0;
    color: rgba(223,232,238,0.88);
    font-family: var(--font-display);
    font-size: 16px;
    font-weight: 400;
    line-height: 1.55;
    white-space: pre-wrap;
  }

  /* ── AI bubble ── */
  .chat-bubble-ai {
    max-width: 88%;
    padding-left: 14px;
    border-left: 1.5px solid color-mix(in srgb, var(--accent-color) 18%, transparent);
  }
  .chat-ai-label {
    margin-bottom: 5px;
    color: var(--accent-color);
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.9;
  }
  .chat-text-ai {
    margin: 0;
    color: #F1F4F6;
    font-family: var(--font-reflective);
    font-size: 17px;
    font-weight: 400;
    line-height: 1.75;
    white-space: pre-wrap;
  }

  /* ── Time separator ── */
  .chat-separator {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 1.5rem 0 1rem;
    text-align: center;
  }
  .chat-separator::before,
  .chat-separator::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(223,232,238,0.07);
  }
  .chat-separator span {
    color: rgba(223,232,238,0.45);
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  /* ── Typing indicator ── */
  .typing-dots {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 18px;
    padding: 2px 0;
  }
  .typing-dot {
    display: block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--accent-color);
    animation: chat-dot-pulse 1.2s ease-in-out infinite;
    opacity: 0.25;
  }

  /* ── Loading / empty states ── */
  .chat-state-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 6rem;
    gap: 0.75rem;
  }
  .chat-breath-line {
    height: 2px;
    width: 2.5rem;
    border-radius: 9999px;
    background: var(--accent-color);
    opacity: 0.4;
    animation: chat-breathe-line 2s ease-in-out infinite;
  }
  .chat-breath-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--accent-color);
    animation: chat-breathe-dot 3s ease-in-out infinite;
  }
  .chat-state-label {
    color: rgba(223,232,238,0.45);
    font-family: var(--font-reflective);
    font-style: italic;
    font-size: 0.9375rem;
  }
  .chat-empty-headline {
    margin: 0;
    color: rgba(223,232,238,0.75);
    font-family: var(--font-reflective);
    font-style: italic;
    font-size: 1.5rem;
  }
  .chat-empty-sub {
    margin: 0;
    color: rgba(223,232,238,0.35);
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    letter-spacing: 0.06em;
  }

  /* ── Image grid ── */
  .chat-img-grid { display: flex; flex-wrap: wrap; gap: 6px; }
  .chat-img-thumb {
    max-width: 200px; max-height: 200px;
    border-radius: 8px; object-fit: cover; cursor: pointer;
    border: 1px solid var(--accent-border);
  }

  /* ── Input bar ── */
  .chat-input-bar {
    flex-shrink: 0;
    border-top: 1px solid rgba(223,232,238,0.07);
    background: rgba(4,6,10,0.88);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .chat-pending-images {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    max-width: 42rem;
    margin: 0 auto;
    padding: 12px 1.25rem 0;
  }
  .chat-pending-thumb-wrap { position: relative; }
  .chat-pending-thumb {
    width: 48px; height: 48px;
    border-radius: 8px; object-fit: cover;
    border: 1px solid var(--accent-border);
  }
  .chat-pending-remove {
    position: absolute; top: -8px; right: -8px;
    background: transparent; border: 0; padding: 0; cursor: pointer;
  }
  .chat-pending-remove-inner {
    display: flex; align-items: center; justify-content: center;
    width: 18px; height: 18px; border-radius: 50%;
    background: rgba(4,6,10,0.85);
    border: 1px solid rgba(223,232,238,0.18);
    color: rgba(223,232,238,0.75);
  }
  .chat-input-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    max-width: 42rem;
    margin: 0 auto;
    padding: 10px 1.25rem 12px;
  }
  .chat-accessories { display: flex; gap: 6px; flex-shrink: 0; }
  .chat-accessory-btn {
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px;
    border-radius: 9px;
    border: 1px solid rgba(223,232,238,0.1);
    background: transparent;
    color: rgba(223,232,238,0.4);
    cursor: pointer;
    transition: border-color 0.18s, color 0.18s, background 0.18s;
  }
  .chat-accessory-btn:disabled { opacity: 0.4; cursor: default; }

  /* Textarea container */
  .chat-textarea-wrap {
    flex: 1;
    display: flex;
    align-items: flex-end;
    border: 1px solid rgba(223,232,238,0.1);
    border-radius: 10px;
    min-height: 38px;
    transition: border-color 0.18s, background 0.18s;
  }
  .chat-textarea {
    width: 100%;
    background: transparent;
    border: 0;
    outline: 0;
    resize: none;
    padding: 8px 12px;
    color: rgba(223,232,238,0.88);
    font-family: var(--font-reflective);
    font-size: 0.9375rem;
    font-weight: 400;
    line-height: 1.4;
    max-height: 100px;
    overflow-y: auto;
  }
  .chat-textarea::placeholder {
    color: rgba(223,232,238,0.28);
    font-style: italic;
  }
  .chat-textarea:disabled { opacity: 0.5; }

  /* Send button */
  .chat-send-btn {
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; flex-shrink: 0;
    border-radius: 9px;
    border: 1px solid rgba(223,232,238,0.1);
    background: transparent;
    cursor: pointer;
    transition: border-color 0.18s, background 0.18s;
  }
  .chat-send-btn:disabled { opacity: 0.35; cursor: default; }

  /* ── Animations ── */
  @keyframes chat-msg-enter {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .chat-msg-entry {
    animation: chat-msg-enter 300ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @keyframes chat-dot-pulse {
    0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
    30%            { opacity: 0.9;  transform: translateY(-3px); }
  }

  @keyframes chat-breathe-line {
    0%, 100% { opacity: 0.2; width: 1.5rem; }
    50%      { opacity: 0.55; width: 2.5rem; }
  }
  @keyframes chat-breathe-dot {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50%      { opacity: 0.8; transform: scale(1.2); }
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .chat-msg-entry  { animation: none; }
    .typing-dot      { animation: none; opacity: 0.6; }
    .chat-breath-line,
    .chat-breath-dot { animation: none; opacity: 0.5; }
  }
`;
